import { Progress, Tooltip } from 'radix-ui';
import styles from './styles.module.scss';
import { useGameDispatch } from '../../../store/global-context';
import { useCurrencyStore } from '../../../store/zustand/useCurrencyStore';
import { upgradesConfig } from '../../../upgrades-config';
import { Debug } from '@game-libs/exc-libs';
import { Icon } from '../../Icon';
import { humanNumber } from '@game-libs/exc-libs';
import { useTranslation } from 'react-i18next';
import { UpgradeActions, UpgradeType } from '../../../types/game-types';
import { SmallButton } from '../SmallButton';
import { costToBuyNItems } from '../../../store/helpers/calculate-cost';
import { useMemo, useRef } from 'react';
import { useUpgradesStore } from '../../../store/zustand/useUpgradesStore';

export const MiniManaMeter = () => {
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const mana = useCurrencyStore((state) => state.mana);
  const gold = useCurrencyStore((state) => state.gold);
  const dispatch = useGameDispatch();
  const meterRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('generators');
  const manaSurgeConfig = useMemo(
    () => upgradesConfig.wizard!.find((upgrade) => upgrade.id === UpgradeType.ManaSurge),
    [],
  );

  const maxMana = upgrades.wizard.manaPool.multiplier;

  // Calculate the manaBoost threshold from upgrades
  const manaBoost = upgrades.wizard.manaBoost?.multiplier ?? 0;

  // Check if mana is above the manaBoost threshold
  const isManaBoosted = mana > manaBoost;

  // ManaSurge upgrade
  const manaSurge = upgrades.wizard.manaSurge;
  const manaSurgeCost = costToBuyNItems(manaSurge.cost.gold!, manaSurge.costGrowthRate, manaSurge.purchased, 1);
  const canAffordManaSurge = gold >= manaSurgeCost;

  const handleManaSurge = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!canAffordManaSurge) return;
    dispatch({ type: UpgradeActions.BUY_UPGRADE, payload: { id: UpgradeType.ManaSurge, entityType: 'wizard' } });

    onPurchaseManaSurge();
  };

  const onPurchaseManaSurge = () => {
    const defaultUnlockAtValue = 3;
    const unlockAmountPurchased =
      manaSurge.purchased + 1 === (manaSurgeConfig!.subTreeConfig?.unlockAt ?? defaultUnlockAtValue);

    if (manaSurge.purchased === 0) {
      dispatch({
        type: UpgradeActions.SHOW_UPGRADE,
        payload: { children: manaSurgeConfig!.children, entityType: 'wizard' },
      });
    }
    if (unlockAmountPurchased) {
      dispatch({
        type: UpgradeActions.UNLOCK_UPGRADE,
        payload: { children: manaSurgeConfig!.children, entityType: 'wizard' },
      });
    }
  };

  return (
    <Tooltip.Provider>
      <Tooltip.Root disableHoverableContent={false} delayDuration={200}>
        <Tooltip.Trigger asChild>
          <div
            ref={meterRef}
            className={`${styles.manaBarContainer}  ${isManaBoosted ? styles.isManaBoosted : ''} ${mana <= 0 ? styles.isManaEmpty : ''}`}
          >
            <Progress.Root className={`${styles.manaBar}`} value={mana} max={maxMana}>
              <Progress.Indicator
                className={`${styles.manaBarValue}`}
                style={{
                  width: `${(mana / maxMana) * 100}%`,
                }}
              />
              <Debug absolute>
                {mana}/{maxMana}
              </Debug>
            </Progress.Root>
            <Icon iconId="mana"></Icon>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal container={document.getElementById('generators-list-container')}>
          <Tooltip.Content className={`game-tooltip ${styles.tooltipContent}`}>
            <div className={styles.tooltipValue}>
              {t('mana')}: {humanNumber(mana, 0)}/{humanNumber(maxMana, 0)}
            </div>
            <div className={styles.tooltipDescription}>{t('manaDescription')}</div>
            {mana <= 0 && <div className={styles.tooltipDescription}>{t('emptyManaDescription')}</div>}
            <div className={styles.rechargeContainer}>
              <SmallButton
                onClick={(event) => handleManaSurge(event as unknown as React.MouseEvent<HTMLButtonElement>)}
                disabled={!canAffordManaSurge}
                type={canAffordManaSurge ? 'golden' : 'gray'}
              >
                <Icon large iconId="plus_blue" />
              </SmallButton>

              <span className={styles.rechargeButton}>
                <Icon iconId="item_cost_gold" />
                {humanNumber(manaSurgeCost, 1)}
              </span>
            </div>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
